// FICHIER : frontend/src/pages/OffreCreate.jsx

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import NavLogo from '../components/NavLogo';

// ── URL du backend ──
const API = 'https://ark-recruitment-backend.onrender.com/api';

// ══════════════════════════════════════════
// ── Icônes ──
// ══════════════════════════════════════════
const IconArrow    = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>);
const IconCopy     = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>);
const IconCheck    = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>);
const IconChevron  = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>);
const IconPlus     = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>);

// ══════════════════════════════════════════
// ── Options par défaut ──
// ══════════════════════════════════════════
const DEFAULT_MODALITES = ['Télétravail', 'Présentiel', 'Hybride'];
const DEFAULT_CONTRATS  = ['CDI', 'CDD', 'Freelance', 'Stage', 'Alternance'];
const DEFAULT_DOMAINES  = ['Tech', 'Finance', 'Droit', 'RH', 'Commercial', 'Marketing', 'Data', 'Design', 'IA', 'Audit', 'Management'];
const DEFAULT_URGENCES  = ['Normal', 'Urgent'];

// ══════════════════════════════════════════
// ── Clés localStorage pour les options custom ──
// ══════════════════════════════════════════
const LS_KEYS = {
  modalites: 'tos_options_modalites',
  contrats:  'tos_options_contrats',
  domaines:  'tos_options_domaines',
  urgences:  'tos_options_urgences',
};

// ── Charger les options depuis localStorage ou defaults ──
function chargerOptions(key, defaults) {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Fusionner defaults + custom sans doublons
      return [...new Set([...defaults, ...parsed])];
    }
  } catch (e) {}
  return defaults;
}

// ── Sauvegarder une nouvelle option custom dans localStorage ──
function sauvegarderOption(key, defaults, nouvelleOption) {
  try {
    const customs = chargerOptions(key, defaults).filter(o => !defaults.includes(o));
    const updated = [...new Set([...customs, nouvelleOption])];
    localStorage.setItem(key, JSON.stringify(updated));
  } catch (e) {}
}

// ══════════════════════════════════════════
// ── Composant Select custom avec option "Créer..." ──
// ══════════════════════════════════════════
function SelectCustom({ label, options, value, onChange, onAjouter, placeholder = 'Sélectionner...' }) {
  const [ouvert,       setOuvert]       = useState(false);
  const [modeCreation, setModeCreation] = useState(false);
  const [nouvelleVal,  setNouvelleVal]  = useState('');
  const ref = useRef(null);

  // ── Fermer si clic extérieur ──
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOuvert(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Confirmer la nouvelle option ──
  const confirmerNouvelle = () => {
    if (!nouvelleVal.trim()) return;
    onAjouter(nouvelleVal.trim());
    onChange(nouvelleVal.trim());
    setNouvelleVal('');
    setModeCreation(false);
    setOuvert(false);
  };

  return (
    <div style={{ marginBottom: 20, position: 'relative' }} ref={ref}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {label}
      </label>

      {/* Déclencheur */}
      <button
        onClick={() => { setOuvert(o => !o); setModeCreation(false); }}
        style={{ width: '100%', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', fontSize: 14, color: value ? 'var(--text)' : 'var(--muted)', fontFamily: 'DM Sans,sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, textAlign: 'left' }}
      >
        <span>{value || placeholder}</span>
        <span style={{ color: 'var(--muted)', flexShrink: 0, transform: ouvert ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
          <IconChevron/>
        </span>
      </button>

      {/* Dropdown */}
      {ouvert && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, zIndex: 100, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>

          {/* Liste des options */}
          {options.map(opt => (
            <button
              key={opt}
              onClick={() => { onChange(opt); setOuvert(false); }}
              style={{ width: '100%', padding: '11px 16px', fontSize: 14, color: value === opt ? 'var(--accent)' : 'var(--text)', background: value === opt ? 'var(--accent-08)' : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'DM Sans,sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              {opt}
              {value === opt && <IconCheck/>}
            </button>
          ))}

          {/* Séparateur + option créer */}
          <div style={{ borderTop: '1px solid var(--border)' }}>
            {!modeCreation ? (
              <button
                onClick={() => setModeCreation(true)}
                style={{ width: '100%', padding: '11px 16px', fontSize: 13, color: 'var(--accent2)', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'DM Sans,sans-serif', display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <IconPlus/> Créer une option...
              </button>
            ) : (
              <div style={{ padding: '10px 12px', display: 'flex', gap: 8 }}>
                <input
                  autoFocus
                  type="text"
                  value={nouvelleVal}
                  onChange={e => setNouvelleVal(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') confirmerNouvelle(); if (e.key === 'Escape') setModeCreation(false); }}
                  placeholder="Nouvelle option..."
                  style={{ flex: 1, background: 'var(--bg2)', border: '1px solid var(--accent)', borderRadius: 6, padding: '7px 10px', fontSize: 13, color: 'var(--text)', outline: 'none', fontFamily: 'DM Sans,sans-serif' }}
                />
                <button
                  onClick={confirmerNouvelle}
                  style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans,sans-serif' }}
                >
                  OK
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════
// ── Composant principal OffreCreate ──
// ══════════════════════════════════════════
export default function OffreCreate() {
  const navigate = useNavigate();

  // ── Détection mobile ──
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // ── Options chargées depuis localStorage ──
  const [optModalites, setOptModalites] = useState(() => chargerOptions(LS_KEYS.modalites, DEFAULT_MODALITES));
  const [optContrats,  setOptContrats]  = useState(() => chargerOptions(LS_KEYS.contrats,  DEFAULT_CONTRATS));
  const [optDomaines,  setOptDomaines]  = useState(() => chargerOptions(LS_KEYS.domaines,  DEFAULT_DOMAINES));
  const [optUrgences,  setOptUrgences]  = useState(() => chargerOptions(LS_KEYS.urgences,  DEFAULT_URGENCES));

  // ── Ajouter une option custom et la persister ──
  const ajouterOption = (key, defaults, setter) => (val) => {
    sauvegarderOption(key, defaults, val);
    setter(prev => [...new Set([...prev, val])]);
  };

  // ── États du formulaire ──
  const [titre,       setTitre]       = useState('');
  const [salaire,     setSalaire]     = useState('');
  const [modalite,    setModalite]    = useState('');
  const [contrat,     setContrat]     = useState('');
  const [domaine,     setDomaine]     = useState('');
  const [urgence,     setUrgence]     = useState('');
  const [description, setDescription] = useState('');
  const [competences, setCompetences] = useState('');

  // ── États génération ──
  const [genere,  setGenere]  = useState(false);
  const [loading, setLoading] = useState(false);
  const [annonce, setAnnonce] = useState('');
  const [copie,   setCopie]   = useState(false);
  const [error,   setError]   = useState('');

  // ── ID de l'offre sauvegardée ──
  const [offreId, setOffreId] = useState(null);
  const [publiee, setPubliee] = useState(false);

  // ── Génération de l'annonce via DeepSeek backend ──
  const genererAnnonce = async () => {
    setError('');
    if (!titre.trim()) {
      setError('Le titre du poste est obligatoire.');
      return;
    }
    setLoading(true);
    try {
      const companyId = localStorage.getItem('company_id') || '';
      const res = await axios.post(`${API}/offre/generer`, {
        company_id: companyId, titre, salaire, modalite, contrat, domaine, urgence, description, competences,
      });
      setAnnonce(res.data.annonce_complete || res.data.titre_optimise);
      setOffreId(res.data.offre_id || null);
      setGenere(true);
    } catch (e) {
      setError('Erreur de génération. Vérifiez que le backend est en ligne.');
    } finally {
      setLoading(false);
    }
  };

  // ── Publication de l'offre ──
  const publierOffre = async () => {
    if (!offreId) return;
    try {
      await axios.post(`${API}/offre/publier/${offreId}`);
      setPubliee(true);
    } catch (e) {}
  };

  // ── Copier dans le presse-papiers ──
  const copierAnnonce = () => {
    navigator.clipboard.writeText(annonce).then(() => {
      setCopie(true);
      setTimeout(() => setCopie(false), 2000);
    });
  };

  // ── Style input réutilisable ──
  const inputStyle = { width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', fontSize: 14, color: 'var(--text)', outline: 'none', fontFamily: 'DM Sans,sans-serif' };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'DM Sans,sans-serif' }}>

      {/* ── NAVIGATION ── */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '0 20px' : '0 48px', height: 64, borderBottom: '1px solid var(--border)', background: 'var(--nav-bg)' }}>
        <NavLogo/>
        {!isMobile && (
          <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 11, color: 'var(--muted)', letterSpacing: 1 }}>
            CRÉER UNE <span style={{ color: 'var(--accent2)' }}>OFFRE</span>
          </div>
        )}
        <span onClick={() => navigate('/dashboard')} style={{ color: 'var(--muted)', fontSize: 13, cursor: 'pointer', fontFamily: 'DM Mono,monospace', letterSpacing: 0.5 }}>
          ← Dashboard
        </span>
      </nav>

      {/* ── CONTENU ── */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: isMobile ? '24px 16px env(safe-area-inset-bottom, 24px)' : '40px 32px' }}>

        {/* En-tête */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 11, color: 'var(--accent2)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>Nouvelle offre</div>
          <h1 style={{ fontFamily: 'Syne,sans-serif', fontSize: isMobile ? 22 : 28, fontWeight: 800, letterSpacing: '-0.8px', marginBottom: 6 }}>
            Créer une offre d'emploi
          </h1>
          <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.6 }}>
            Remplissez le formulaire — l'IA génère votre annonce formatée prête à publier.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 0 : 32 }}>

          {/* ── COLONNE GAUCHE — Formulaire ── */}
          <div>

            {/* Titre */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                Titre du poste *
              </label>
              <input type="text" value={titre} onChange={e => setTitre(e.target.value)} placeholder="Ex : Développeur React Senior" style={inputStyle}/>
            </div>

            {/* Salaire */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                Salaire / Rémunération
              </label>
              <input type="text" value={salaire} onChange={e => setSalaire(e.target.value)} placeholder="Ex : 800k – 1,2M FCFA/mois" style={inputStyle}/>
            </div>

            {/* Modalité */}
            <SelectCustom
              label="Modalité de travail"
              options={optModalites}
              value={modalite}
              onChange={setModalite}
              placeholder="Sélectionner une modalité..."
              onAjouter={ajouterOption(LS_KEYS.modalites, DEFAULT_MODALITES, setOptModalites)}
            />

            {/* Contrat */}
            <SelectCustom
              label="Type de contrat"
              options={optContrats}
              value={contrat}
              onChange={setContrat}
              placeholder="Sélectionner un contrat..."
              onAjouter={ajouterOption(LS_KEYS.contrats, DEFAULT_CONTRATS, setOptContrats)}
            />

            {/* Domaine */}
            <SelectCustom
              label="Domaine"
              options={optDomaines}
              value={domaine}
              onChange={setDomaine}
              placeholder="Sélectionner un domaine..."
              onAjouter={ajouterOption(LS_KEYS.domaines, DEFAULT_DOMAINES, setOptDomaines)}
            />

            {/* Urgence */}
            <SelectCustom
              label="Urgence"
              options={optUrgences}
              value={urgence}
              onChange={setUrgence}
              placeholder="Niveau d'urgence..."
              onAjouter={ajouterOption(LS_KEYS.urgences, DEFAULT_URGENCES, setOptUrgences)}
            />

            {/* Description */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                Description du poste
              </label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Décrivez le rôle, les responsabilités, le contexte..." rows={5} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}/>
            </div>

            {/* Compétences */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                Compétences requises
              </label>
              <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8, lineHeight: 1.5 }}>Une compétence par ligne</p>
              <textarea value={competences} onChange={e => setCompetences(e.target.value)} placeholder={"React / Node.js\nBonne communication\n3 ans d'expérience minimum"} rows={4} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.8 }}/>
            </div>

            {/* Erreur */}
            {error && (
              <div style={{ background: 'rgba(220,38,38,0.04)', border: '1px solid rgba(220,38,38,0.12)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--error)', marginBottom: 16 }}>
                {error}
              </div>
            )}

            {/* Bouton générer */}
            <button
              onClick={genererAnnonce}
              disabled={loading}
              style={{ width: '100%', background: loading ? 'var(--border)' : 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10, padding: '13px 20px', fontSize: 15, fontWeight: 600, fontFamily: 'DM Sans,sans-serif', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              {loading ? 'Génération en cours…' : (<>Générer l'annonce <IconArrow/></>)}
            </button>
          </div>

          {/* ── COLONNE DROITE — Aperçu ── */}
          <div style={{ marginTop: isMobile ? 32 : 0 }}>
            <div style={{ position: isMobile ? 'relative' : 'sticky', top: isMobile ? 0 : 80 }}>
              <div style={{ background: 'var(--bg3)', border: `1px solid ${genere ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 14, overflow: 'hidden' }}>

                {/* Header aperçu */}
                <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg2)' }}>
                  <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 11, color: genere ? 'var(--accent2)' : 'var(--muted)', letterSpacing: 1, textTransform: 'uppercase' }}>
                    {genere ? 'Annonce générée' : 'Aperçu'}
                  </div>
                  {genere && (
                    <button
                      onClick={copierAnnonce}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, background: copie ? 'rgba(22,163,74,0.08)' : 'var(--accent)', color: copie ? 'var(--success)' : '#fff', border: copie ? '1px solid var(--success)' : 'none', borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans,sans-serif', transition: 'all 0.2s' }}
                    >
                      {copie ? <><IconCheck/> Copié !</> : <><IconCopy/> Copier</>}
                    </button>
                  )}
                </div>

                {/* Corps aperçu */}
                <div style={{ padding: '20px 18px', minHeight: 300 }}>
                  {!genere && !loading && (
                    <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--muted)' }}>
                      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--border)' }}>
                          <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                        </svg>
                      </div>
                      <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 12, letterSpacing: 1 }}>
                        Remplissez le formulaire<br/>et cliquez sur Générer
                      </div>
                    </div>
                  )}
                  {loading && (
                    <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--muted)', fontFamily: 'DM Mono,monospace', fontSize: 12, letterSpacing: 1 }}>
                      Génération en cours…
                    </div>
                  )}
                  {genere && annonce && (
                    <pre style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.9, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontFamily: 'DM Sans,sans-serif' }}>
                      {annonce}
                    </pre>
                  )}
                </div>
              </div>

              {/* Bouton publier */}
              {genere && offreId && (
                <button
                  onClick={publierOffre}
                  disabled={publiee}
                  style={{ width: '100%', marginTop: 12, background: publiee ? 'var(--success)' : 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 20px', fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans,sans-serif', cursor: publiee ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                >
                  {publiee ? 'Offre publiée !' : 'Publier l\'offre'}
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}